<template>
    <div id="navigation">
        <div class="home-wrapper">
            <div class="nav-button nav-button-home" title="Home" @click="toDefaultView()">
                <img class="nav-button-icon" :src="getMedia('icons/home.svg')" alt="">
            </div>
        </div>
        <div class="states-wrapper">
            <div v-if="availableSegments && availableSegments.water" class="nav-button nav-button-category" :class="{'nav-button-disable' : !isWaterEnabled}" @click="toggleWaterBool()" @mouseover="enableCategory('Wasser',true)" @mouseleave="enableCategory('Wasser',false)">
                <img v-if="isWaterEnabled" class="nav-button-icon" :src="getMedia('icons/ic_water.svg')" alt="">
                <img v-else class="nav-button-icon" :src="getMedia('icons/ic_water_blue.svg')" alt="">
                <div class="nav-button-text">{{t("Water")}}</div>
            </div>
            <div v-if="availableSegments && availableSegments.gas" class="nav-button nav-button-category" :class="{'nav-button-disable' : !isGasEnabled}" @click="toggleGasBool()"  @mouseover="enableCategory('Gas',true)" @mouseleave="enableCategory('Gas',false)">
                <img v-if="isGasEnabled" class="nav-button-icon" :src="getMedia('icons/ic_fire.svg')" alt="">
                <img v-else class="nav-button-icon" :src="getMedia('icons/ic_fire_blue.svg')" alt="">
                <div class="nav-button-text">{{t("Gas")}}</div>
            </div>
            <div v-if="availableSegments && availableSegments.sewage" class="nav-button nav-button-category" :class="{'nav-button-disable' : !isSewageEnabled}" @click="toggleSewageBool()"  @mouseover="enableCategory('Abwasser',true)" @mouseleave="enableCategory('Abwasser',false)">
                <img v-if="isSewageEnabled" class="nav-button-icon" :src="getMedia('icons/ic_pipe.svg')" alt="">
                <img v-else class="nav-button-icon" :src="getMedia('icons/ic_pipe_blue.svg')" alt="">
                <div class="nav-button-text" style="margin-left: 3px;">{{t("Sewage")}}</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { getMedia } from "@/util";
import { useStore } from '@/store';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

// BabylonJS Imports
import { Animation } from "@babylonjs/core/Animations/animation";
import { CubicEase, EasingFunction } from "@babylonjs/core/Animations/easing";
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { ArcRotateCameraKeyboardMoveInput } from '@babylonjs/core/Cameras/Inputs/arcRotateCameraKeyboardMoveInput';

const store = useStore();
const { isWaterEnabled, isSewageEnabled, isGasEnabled, availableSegments, versionData, isMobile, defaultCamStats } = storeToRefs(store);

const { t } = useI18n();

const toggleWaterBool = () => store.toggleWaterBool();
const toggleSewageBool = () => store.toggleSewageBool();
const toggleGasBool = () => store.toggleGasBool();

const enableCategory = (name: string, mouseover: boolean) => {
    if (!isMobile.value) {
        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

        const scene = window.scene;
        const node = scene.getNodeByName(name);
        
        if (node) {
            node.getChildMeshes().forEach((mesh) => {
                if (mesh.material && mesh.material.name != "steeDarkl" && mesh.material.name != "steel") {
                    if (name != "Gas" || mesh.material.name != "darkPlastic") {
                        Animation.CreateAndStartAnimation("", mesh.material, "alpha", 60, 20, mesh.material.alpha, mouseover ? 0.6 : 1, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
                    }
                }
            });
        }
    }
};

const toDefaultView = () => {
    store.setActiveAreaID(null);
    store.setWaterBool(availableSegments.value!.water);
    store.setSewageBool(availableSegments.value!.sewage);
    store.setGasBool(availableSegments.value!.gas);
    
    // Move Camera to default position
    const scene = window.scene;
    const camera = scene.activeCamera as ArcRotateCamera;

    animateCamerarideToDefault(2000);

    if (versionData.value && versionData.value.areas) {
        versionData.value.areas.forEach(area => {
            setPinGroupVisible("Pins_" + area.nodeName, 0, [], 0);
        });
    }

    setTimeout(() => {
        setPinGroupVisible("Pins_Orbit", 1, []);
        
        setEnvironmentVisible(1);

        camera.lowerRadiusLimit = defaultCamStats.value.radius.lowerLimit;
        camera.upperRadiusLimit = defaultCamStats.value.radius.upperLimit;
        camera.lowerAlphaLimit = defaultCamStats.value.alpha.lowerLimit;
        camera.upperAlphaLimit = defaultCamStats.value.alpha.upperLimit;
        camera.lowerBetaLimit = defaultCamStats.value.beta.lowerLimit;
        camera.upperBetaLimit = defaultCamStats.value.beta.upperLimit;
    }, 1200);

    camera.invertRotation = false;

    const navigationUI = document.getElementById("navigation");
    if (navigationUI) {
        navigationUI.style.opacity = "0";
        navigationUI.style.pointerEvents = "none";
    }

    store.setToolTipsDisabled(true)
    store.setInfoPanelOpen(false);
};

const animateCamerarideToDefault = (time: number) => {
    const scene = window.scene;
    const camera = scene.activeCamera as ArcRotateCamera;
    
    // Keyboard controls
    if(camera.inputs && camera.inputs.attached.keyboard) {
        let keyboardInputs = camera.inputs.attached.keyboard as ArcRotateCameraKeyboardMoveInput;
        keyboardInputs.keysDown = [38];
        keyboardInputs.keysUp = [40];
        keyboardInputs.keysLeft = [39];
        keyboardInputs.keysRight = [37];
    }

    // Open Limit ranges
    camera.lowerRadiusLimit = camera.upperRadiusLimit = null;
    camera.lowerAlphaLimit = camera.upperAlphaLimit = null;
    camera.lowerBetaLimit = 0
    camera.upperBetaLimit = Math.PI;
    camera.invertRotation = true;
        
    const ease = new CubicEase();
    ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
    
    const framePerSecond = 60;
    const totalFrames = time * framePerSecond / 1000;

    const alphaAnimation = new Animation("", "alpha", framePerSecond, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
    alphaAnimation.setEasingFunction(ease);
    alphaAnimation.setKeys([
        {frame: 0, value: camera.alpha % (Math.PI*2)},
        {frame: totalFrames, value: defaultCamStats.value.alpha.value}
    ]);

    const betaAnimation = new Animation("", "beta", framePerSecond, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
    betaAnimation.setEasingFunction(ease);
    betaAnimation.setKeys([
        {frame: 0, value: camera.beta % (Math.PI*2)},
        {frame: totalFrames/1.5, value: defaultCamStats.value.beta.value},
        {frame: totalFrames, value: defaultCamStats.value.beta.value}
    ]);

    const radiusAnimation = new Animation("", "radius", framePerSecond, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
    radiusAnimation.setEasingFunction(ease);
    radiusAnimation.setKeys([
        {frame: 0, value: camera.radius},
        {frame: totalFrames, value: defaultCamStats.value.radius.value}
    ]);

    const targetAnimation = new Animation("", "target", framePerSecond, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CYCLE);
    targetAnimation.setEasingFunction(ease);
    targetAnimation.setKeys([
        {frame: 0, value: camera.target},
        {frame: totalFrames, value: defaultCamStats.value.target}
    ]);
    
    camera.animations = [targetAnimation, radiusAnimation, alphaAnimation, betaAnimation];
    scene.beginAnimation(camera, 0, totalFrames); 
};

const setPinVisiblity = (name: string, visibility: number, time: number = 40) => {
    const ease = new CubicEase();
    ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

    const scene = window.scene;
    const pin = scene.getMeshByName(name);
    
    if (pin) {
        if (visibility != 0) pin.setEnabled(visibility != 0);
        Animation.CreateAndStartAnimation("", pin, "visibility", 60, time, pin.visibility, visibility, Animation.ANIMATIONLOOPMODE_CONSTANT, ease, () => {
            pin.setEnabled(visibility != 0);
        }); 
    }
};

const setPinGroupVisible = (name: string, visibility: number, exceptions: string[], time: number = 40) => {
    const scene = window.scene;
    const group = scene.getNodeByName(name);
    
    if (group) {
        group.getChildren().forEach((child) => {
            if (!exceptions.includes(child.name)) {
                setPinVisiblity(child.name, visibility, time);
            }
        });
    }
};

const setEnvironmentVisible = (visibility: number, time: number = 40) => {
    const scene = window.scene;
    const environmentNode = scene.getNodeByName("Environment");
    
    if (environmentNode) {
        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
        
        environmentNode.getChildMeshes().forEach((mesh) => {
            Animation.CreateAndStartAnimation("", mesh, "visibility", 60, time, mesh.visibility, visibility, Animation.ANIMATIONLOOPMODE_CONSTANT, ease);
        });
    }
};
</script>

<style>
#navigation{
    display: inline-flex;
    opacity: 0;
    pointer-events: none;
    margin: 15px;
    transition: opacity 1s ease;

    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}
#navigation>*:not(:last-child){
    margin-right: 1rem;
}
#navigation .states-wrapper{
    display: flex;
    border-radius: 10rem;
    padding: 5px;
    background-color: var(--bg-light);
}
#navigation .states-wrapper button:not(:last-child){
    margin-right: 0.5rem;
}
#navigation .home-wrapper{
    display: flex;
    align-items: center;
    margin-right: 10px;
}

.nav-button{
    display: flex;
    align-items: center;    
    padding: 5px 16px 5px 10px;
    margin-right: 6px;
    border-radius: 50px;
    border: 1px solid var(--bg-light);
    cursor: pointer;
    color: white;
}
.nav-button:last-child{
    margin-right: 0px; 
}
.nav-button-home{
    background: var(--bg-light);
    transition: background .2s ease, border-color .2s ease;
    height: 43px;
    width: 43px;
    padding: 0;
    justify-content: center;
}
.nav-button-home:hover{
    background: rgb(210,210,210);
    border-color: rgb(210,210,210);
}
.nav-button-category{
    background: linear-gradient(90deg,var(--color-lightblue) 20%,var(--color-blue) 90%);
}
.nav-button-category:hover{
    background: linear-gradient(90deg,#2682c477 20%,#2248a177 90%);
}
.nav-button-icon{
    height: 24px;
    width: 24px;
}
.nav-button-text{
    font-size: 16px;
}  
.nav-button-disable{
    background: transparent;
    border-color: var(--bg-dark);
    color: var(--bg-dark);
}
.nav-button-disable:hover{
    background: linear-gradient(90deg,#2682c428 20%,#2248a12a 90%);
    border-color: var(--color-blue);
    color: var(--color-blue);
}

.homeIcon{
    background-image: url('/src/assets/icons/home.svg');
    opacity: 0.7;
    width: 25px;
    height: 25px;
    background-size: 100%;
}
.waterIcon{
    background-image: url('/src/assets/icons/ic_water.svg');
}
.sewageIcon{
    background-image: url('/src/assets/icons/ic_pipe.svg');
}
.gasIcon{
    background-image: url('/src/assets/icons/ic_fire.svg');
}
.nav-button-disable .waterIcon{
    background-image: url('/src/assets/icons/ic_water_blue.svg');
}
.nav-button-disable .sewageIcon{
    background-image: url('/src/assets/icons/ic_pipe_blue.svg');
}
.nav-button-disable .gasIcon{
    background-image: url('/src/assets/icons/ic_fire_blue.svg');
}

@media (max-width: 900px){
    .nav-button-category:hover{
        background: linear-gradient(90deg,var(--color-lightblue) 20%,var(--color-blue) 90%);
    }
    .nav-button-disable{
        background: transparent !important;
        border-color: var(--bg-dark) !important;
        color: var(--bg-dark) !important;
    }
    .nav-button-text{
        font-size: 13px;
    }  
}
</style>